// Studio — image generation. Phase 1 wires: composer + live candidate
// strip (SSE-driven) + gallery + detail modal + per-user settings
// persistence. Reference image (Phase 2) and inpaint (Phase 3) come
// later — those are bigger surfaces and out of scope for the daily
// driver pass.

import { useEffect, useState } from "preact/hooks";
import "../../styles/studio.css";

import { Icon } from "../../icons";
import { useImageGen } from "../../lib/use-image-gen";
import type { GalleryItem } from "../../lib/rpc";
import { Composer } from "./composer";
import { Candidates } from "./candidates";
import { Gallery, GalleryDetail } from "./gallery";
import { InpaintEditor } from "./inpaint";

interface StudioViewProps {
  notify: (msg: string) => void;
  /** One-shot hint from the parent (e.g. "user picked Gallery from
   *  the avatar menu" → start on the Gallery pane instead of the
   *  default Imagine). Consumed on mount via onConsumeStartPane so
   *  subsequent pane toggles don't snap back. */
  startPane?: Pane | null;
  onConsumeStartPane?: () => void;
}

type Pane = "imagine" | "gallery";

export function StudioView({ notify, startPane, onConsumeStartPane }: StudioViewProps) {
  const gen = useImageGen();
  const [pane, setPane] = useState<Pane>(startPane || "imagine");
  const [selected, setSelected] = useState<GalleryItem | null>(null);
  const [inpaintSource, setInpaintSource] = useState<GalleryItem | null>(null);

  // Apply + clear the startPane hint. We do this in an effect so the
  // parent's setState (clearing the hint) doesn't fight our initial
  // render. Only runs when the hint actually changes; consumes once.
  useEffect(() => {
    if (startPane) {
      setPane(startPane);
      onConsumeStartPane?.();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startPane]);

  // Surface hook-level errors as toasts.
  if (gen.error) notify(gen.error);

  const onSubmit = (args: Parameters<typeof gen.generate>[0]) => {
    gen.generate(args);
  };

  return (
    <div className="studio-shell">
      <header className="studio-head">
        <h1 className="studio-title">Studio</h1>
        <div className="pill-mini studio-pane-pill">
          <button data-active={pane === "imagine"} onClick={() => setPane("imagine")} type="button">Imagine</button>
          <button data-active={pane === "gallery"} onClick={() => setPane("gallery")} type="button">Gallery</button>
        </div>
      </header>

      {pane === "imagine" ? (
        <>
          <Composer
            options={gen.options}
            prefs={gen.prefs}
            setPrefs={gen.setPrefs}
            generating={gen.generating}
            onSubmit={onSubmit}
            notify={notify}
          />

          <Candidates slots={gen.slots} onOpen={setSelected} />

          {gen.slots.length === 0 && gen.gallery.length > 0 && (
            <div className="studio-recent">
              <div className="studio-recent-head">
                <span className="eyebrow">Recent</span>
              </div>
              <Gallery
                items={gen.gallery.slice(0, 12)}
                loading={gen.galleryLoading}
                onOpen={setSelected}
              />
            </div>
          )}
        </>
      ) : (
        <Gallery
          items={gen.gallery}
          loading={gen.galleryLoading}
          onOpen={setSelected}
        />
      )}

      {selected && (
        <GalleryDetail
          item={selected}
          onClose={() => setSelected(null)}
          onDelete={async () => {
            await gen.removeFromGallery(selected.id);
            notify("Removed");
            setSelected(null);
          }}
          onInpaint={() => {
            setInpaintSource(selected);
            setSelected(null);
          }}
        />
      )}

      {inpaintSource && (
        <InpaintEditor
          source={inpaintSource}
          notify={notify}
          onClose={() => setInpaintSource(null)}
          onCommit={async (args) => {
            const item = await gen.inpaint(args);
            if (item) {
              notify("Inpaint complete");
              setInpaintSource(null);
              setSelected(item);
            } else {
              notify("Inpaint failed");
            }
            return item;
          }}
        />
      )}

      {pane === "gallery" && (
        <button
          className="icon-btn studio-refresh"
          onClick={() => gen.refreshGallery()}
          title="Refresh"
          type="button"
        >
          <Icon name="more" size={14} />
        </button>
      )}
    </div>
  );
}
